import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  getSubTaskTemplates,
  IExperience,
  IExperienceTemplate,
  RequirementType,
  SubTaskStatus,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import assertNever from 'assert-never';
import { compact, sortBy } from 'lodash';
import React, { useMemo } from 'react';
import { getFixedTimingOrApproximateParentTiming } from '../../../../services/experiences/timing';
import { FullTheme } from '../../../../themes';

type CompleteSubTaskStates =
  | 'NOT_STARTED'
  | 'LATE'
  | 'OPEN_BUT_NOT_REQUIRED_ANYMORE'
  | 'PAST_DEADLINE'
  | SubTaskStatus;

const Grid = styled('div')<{ cols: number; height: number | string }>((p) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${p.cols}, 1fr)`,
  height: p.height,
  overflow: 'hidden',
  borderRadius: p.theme.custom.border.radius()
}));

const GridItem = styled('div')<{ color: string; col: number }>((p) => ({
  backgroundColor: p.color,
  gridColumn: p.col
}));

const toColor = (
  state: CompleteSubTaskStates,
  colors: FullTheme['custom']['colors']
) => {
  switch (state) {
    case 'NOT_STARTED':
      return colors.gray.w300;
    case 'LATE':
      return colors.red.w400;
    case 'OPEN_BUT_NOT_REQUIRED_ANYMORE':
      return colors.green.w400;
    case SubTaskStatus.DONE:
      return colors.green.w400;
    case SubTaskStatus.OPEN:
      return colors.blue.w400;
    case SubTaskStatus.SKIPPED:
      return colors.gray.w500;
    case 'PAST_DEADLINE':
      return colors.gray.w500;
    default:
      return assertNever(state);
  }
};

const toCompleteSubTaskStatus = (
  t: TaskStatus,
  s: SubTaskStatus,
  requirementType: RequirementType
) => {
  const result =
    t === TaskStatus.NOT_STARTED
      ? s === SubTaskStatus.DONE || s === SubTaskStatus.SKIPPED // for items completed ahead of time
        ? s
        : 'NOT_STARTED'
      : t === TaskStatus.LATE && s === SubTaskStatus.OPEN
      ? 'LATE'
      : (t === TaskStatus.PAST_DEADLINE && s === SubTaskStatus.OPEN) ||
        (t === TaskStatus.COMPLETE &&
          s === SubTaskStatus.OPEN &&
          requirementType === RequirementType.OPTIONAL)
      ? 'PAST_DEADLINE' // when TaskCompletionCriteria are present and only a subset needed to be done
      : t === TaskStatus.COMPLETE && s === SubTaskStatus.OPEN
      ? 'OPEN_BUT_NOT_REQUIRED_ANYMORE'
      : s;
  return result;
};

export const InstanceStatusBar = React.memo(
  ({
    d,
    tpl,
    actor,
    height = '1rem'
  }: {
    d: Doc<IExperience>;
    tpl: Doc<IExperienceTemplate>;
    actor: string;
    height?: string | number;
  }) => {
    const theme = useTheme();
    const { tasks } = d.data;
    const subTaskStates = useMemo<CompleteSubTaskStates[]>(() => {
      const statesByTplId = tasks.reduce<{
        [id: string]: {
          taskStatus: TaskStatus;
          subTaskStatus: SubTaskStatus;
          requirementType: RequirementType;
        };
      }>((m, task) => {
        task.subTasks.forEach((s) => {
          m[s.templateId] = {
            taskStatus: task.status,
            subTaskStatus: s.status,
            requirementType: s.requirementType
          };
        });
        return m;
      }, {});
      // TODO Triggered Items - they probably show up here just as well or don't they?
      const sortedSubTaskIds = sortBy(
        tpl.data.tasks.filter((tTpl) => tTpl.actor === actor),
        (taskTpl) =>
          getFixedTimingOrApproximateParentTiming(
            taskTpl.timing,
            tpl.data.tasks
          )
      ).reduce<string[]>((m, taskTpl) => {
        return m.concat(getSubTaskTemplates(taskTpl).map((s) => s.id));
      }, []);
      return compact(
        sortedSubTaskIds.map((sId) => {
          const state = statesByTplId[sId];
          return state
            ? toCompleteSubTaskStatus(
                state.taskStatus,
                state.subTaskStatus,
                state.requirementType
              )
            : null;
        })
      );
    }, [tasks, tpl, actor]);
    return (
      <Grid cols={subTaskStates.length} height={height}>
        {subTaskStates.map((state, i) => (
          <GridItem
            key={i}
            color={toColor(state, theme.custom.colors)}
            col={i + 1}
          />
        ))}
      </Grid>
    );
  }
);
